import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, MapPin, Phone, Music2, Camera, Star, MapPinned, Globe } from "lucide-react";
import Logo from "./Logo";

// Centralized social/listing graph — used by footer + SEO sameAs schema
export const SOCIAL_LINKS = [
  { name: "Instagram · @onyxrooftopdc", url: "https://instagram.com/onyxrooftopdc", icon: Instagram, primary: true, handle: "@onyxrooftopdc" },
  { name: "Instagram · @onyxgemsdc", url: "https://instagram.com/onyxgemsdc", icon: Instagram, primary: true, handle: "@onyxgemsdc" },
  { name: "Facebook", url: "https://www.facebook.com/onyxrooftopdc/", icon: Facebook, primary: true, handle: "Facebook" },
  { name: "TikTok", url: "https://www.tiktok.com/discover/onyx-rooftop-dc", icon: Music2, primary: true, handle: "TikTok" },
  { name: "Snapchat", url: "https://www.snapchat.com/place/onyxrooftoplounge/e9e65d14-ac47-11f0-8e44-972f2ba68100", icon: Camera, primary: true, handle: "Snapchat" },
];

export const LISTING_LINKS = [
  { name: "Yelp", url: "https://www.yelp.com/biz/onyx-rooftop-lounge-washington", icon: Star },
  { name: "Apple Maps", url: "https://maps.apple.com/place?map=h&place-id=I4ECB6800E67BF482&address=1815+M+St+NW%2C+Washington%2C+DC++20036%2C+United+States&coordinate=38.905950%2C-77.042405&name=Onyx+Rooftop+Lounge&_provider=9902", icon: MapPinned },
  { name: "MapQuest", url: "https://www.mapquest.com/us/district-of-columbia/onyx-rooftop-lounge-778799354", icon: MapPinned },
  { name: "Facebook Photos", url: "https://www.facebook.com/onyxrooftopdc/photos", icon: Camera },
  { name: "BarDC", url: "https://bardc.com/bars/onyx-rooftop-lounge/", icon: Globe },
  { name: "Source of the Spring", url: "https://www.sourceofthespring.com/venue/onyx-rooftop-lounge/", icon: Globe },
  { name: "PoPville Feature", url: "https://www.popville.com/2024/11/onyx-rooftop-lounge-dc-m-street-nw-downtown/", icon: Globe },
];

export default function Footer({ siteSettings }) {
  const s = siteSettings || {};

  return (
    <footer data-testid="footer" className="relative bg-ink text-white mt-0 pt-20 pb-10 overflow-hidden">
      {/* Subtle gold glow accent */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--onyx-gold)]/30 to-transparent" />

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-14">
          <div className="md:col-span-2">
            <Logo variant="light" size="lg" showTagline={true} />
            <p className="text-white/55 max-w-md leading-relaxed mt-8">
              Washington DC's premier rooftop nightlife destination. Curated luxury experiences,
              premium bottle service, and elite entertainment.
            </p>

            {/* Futuristic social orb row — primary platforms */}
            <div className="mt-8" data-testid="footer-socials">
              <p className="text-[11px] tracking-[0.32em] uppercase font-semibold mb-5" style={{ color: "var(--onyx-gold)" }}>+ Follow the rooftop</p>
              <div className="flex flex-wrap gap-3.5">
                {SOCIAL_LINKS.map((s) => (
                  <a
                    key={s.handle}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={s.name}
                    data-testid={`social-${s.handle.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
                    className="onyx-orb group"
                  >
                    <s.icon size={22} strokeWidth={1.7} className="relative z-10" />
                    <span className="onyx-orb-glow" />
                  </a>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-5 text-sm font-medium">
                <span style={{ color: "var(--onyx-gold)" }}>@onyxrooftopdc</span>
                <span className="text-white/25">·</span>
                <span style={{ color: "var(--onyx-gold)" }}>@onyxgemsdc</span>
              </div>
            </div>
          </div>

          <div>
            <p className="eyebrow mb-5">Explore</p>
            <ul className="space-y-3 text-sm text-white/65">
              <li><Link to="/events" className="hover:text-[var(--onyx-gold)] transition-colors">Upcoming Events</Link></li>
              <li><Link to="/reservations" className="hover:text-[var(--onyx-gold)] transition-colors">Reservations</Link></li>
              <li><Link to="/vip" className="hover:text-[var(--onyx-gold)] transition-colors">VIP Experience</Link></li>
              <li><Link to="/menu" className="hover:text-[var(--onyx-gold)] transition-colors">Menu</Link></li>
              <li><Link to="/gallery" className="hover:text-[var(--onyx-gold)] transition-colors">Gallery</Link></li>
              <li><Link to="/blog" className="hover:text-[var(--onyx-gold)] transition-colors">Blog</Link></li>
              <li><Link to="/careers" className="hover:text-[var(--onyx-gold)] transition-colors">Careers</Link></li>
            </ul>
          </div>

          <div>
            <p className="eyebrow mb-5">Contact</p>
            <ul className="space-y-4 text-sm text-white/65">
              <li className="flex items-start gap-3"><MapPin size={14} className="text-[var(--onyx-gold)] mt-1 flex-shrink-0" />{s.address || "1815 M Street NW, Washington, DC 20036"}</li>
              <li className="flex items-start gap-3"><Phone size={14} className="text-[var(--onyx-gold)] mt-1 flex-shrink-0" /><a href="tel:+12028788151" className="hover:text-[var(--onyx-gold)]">{s.phone || "(202) 878-8151"}</a></li>
              <li className="flex items-start gap-3"><Mail size={14} className="text-[var(--onyx-gold)] mt-1 flex-shrink-0" /><a href="mailto:info@onyxrooftopdc.com" className="hover:text-[var(--onyx-gold)] break-all">info@onyxrooftopdc.com</a></li>
              <li className="flex items-start gap-3"><Mail size={14} className="text-[var(--onyx-gold)] mt-1 flex-shrink-0" /><a href="mailto:reservations@onyxrooftopdc.com" className="hover:text-[var(--onyx-gold)] break-all">reservations@onyxrooftopdc.com</a></li>
            </ul>
          </div>
        </div>

        {/* Featured-on row — bold and impossible to miss */}
        <div className="relative mb-12" data-testid="footer-listings">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--onyx-gold)]/50 to-transparent" />
          <div className="pt-10 pb-10 bg-gradient-to-b from-[var(--onyx-gold)]/[0.04] to-transparent rounded-sm">
            <p className="text-center md:text-left text-xs tracking-[0.42em] uppercase font-semibold mb-2" style={{ color: "var(--onyx-gold)" }}>★ Featured On</p>
            <h3 className="font-display text-2xl md:text-3xl text-white mb-7 text-center md:text-left leading-tight">
              Find Onyx <span className="text-[var(--onyx-gold)]">everywhere</span>.
            </h3>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2.5 md:gap-3">
              {LISTING_LINKS.map((l) => (
                <a
                  key={l.name}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`listing-${l.name.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
                  className="group flex items-center gap-2 px-4 py-2.5 border border-white/15 hover:border-[var(--onyx-gold)] bg-white/[0.02] hover:bg-[var(--onyx-gold)] text-sm tracking-[0.12em] uppercase font-medium text-white/80 hover:text-black transition-all duration-300 hover:-translate-y-0.5"
                >
                  <l.icon size={14} strokeWidth={1.7} className="text-[var(--onyx-gold)] group-hover:text-black transition-colors" />
                  {l.name}
                </a>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[var(--onyx-gold)]/50 to-transparent" />
        </div>

        <div className="h-px bg-white/10 mb-8" />
        <div className="mb-6 px-4 py-3 bg-white/[0.03] border border-white/10 rounded-sm" data-testid="accessibility-notice">
          <p className="text-[11px] tracking-[0.18em] uppercase font-semibold mb-1" style={{ color: "var(--onyx-gold)" }}>Accessibility Notice</p>
          <p className="text-xs text-white/60 leading-relaxed">
            Onyx Rooftop Lounge is located on the <span className="text-white/85 font-medium">3rd and 4th floors</span> of the building and, unfortunately,
            <span className="text-white/85 font-medium"> the venue is not wheelchair accessible — there is no elevator on site.</span> All guests must
            use the stairs to access the lounge and rooftop. We apologize for the inconvenience.
          </p>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-white/40">
          <p>© {new Date().getFullYear()} Onyx Rooftop Lounge. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 items-center">
            <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-[var(--onyx-gold)] tracking-luxe-tight uppercase">Admin</Link>
            <span className="tracking-luxe-tight uppercase">Washington · DC · Dupont After Dark</span>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-white/5 text-center text-[11px] text-white/35 tracking-luxe-tight">
          Website and SEO built and managed by{" "}
          <a
            href="https://webnehtech.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-credit"
            className="text-white/60 hover:text-[var(--onyx-gold)] transition-colors underline-offset-4 hover:underline"
          >
            WebnehTech.com
          </a>
        </div>
      </div>

      <style>{`
        .onyx-orb {
          position: relative;
          width: 52px;
          height: 52px;
          border: 1.5px solid rgba(255,255,255,0.25);
          color: rgba(255,255,255,0.9);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: rgba(255,255,255,0.03);
          transition: color 280ms, border-color 280ms, transform 280ms, background 280ms, box-shadow 280ms;
          overflow: hidden;
          box-shadow: 0 0 0 0 rgba(212,175,55,0);
        }
        .onyx-orb:hover {
          color: #0A0A0A;
          border-color: var(--onyx-gold);
          background: var(--onyx-gold);
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 8px 28px -6px rgba(212,175,55,0.6);
        }
        .onyx-orb-glow {
          position: absolute;
          inset: -6px;
          border-radius: inherit;
          background: radial-gradient(circle, rgba(212,175,55,0.55) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 320ms;
          z-index: 0;
        }
        .onyx-orb:hover .onyx-orb-glow {
          opacity: 1;
        }
      `}</style>
    </footer>
  );
}
